import { useMemo, useRef, useState } from 'react';
import type { AppState, Barcode, Can } from '../../shared/api';
import { countryCode } from '../../shared/countries';
import { CanImage } from '../components/CanImage';
import { Sheet } from '../components/Sheet';
import { useBarcodes, useDeleteBarcode, useUploadCanImage } from '../api';
import { searchCans } from '../../shared/search';
import { sizeLabel } from '../format';

interface Props {
  state: AppState;
  onAddToWall: (canId: number) => void;
  onWanted: (canId: number, wanted: boolean) => void;
  onNewFlavor: () => void;
  onError: (message: string) => void;
  onBack: () => void;
}

/** Every flavor in the catalog, with its barcodes. */
export function Database({ state, onAddToWall, onWanted, onNewFlavor, onError, onBack }: Props) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('alle');
  const [openId, setOpenId] = useState<number | null>(null);
  const barcodes = useBarcodes();

  const owned = useMemo(() => new Set(state.entries.map((e) => e.canId)), [state.entries]);
  const wanted = useMemo(() => new Set(state.wanted.map((w) => w.canId)), [state.wanted]);
  const eansByCan = useMemo(() => {
    const map = new Map<number, Barcode[]>();
    for (const b of barcodes.data ?? []) map.set(b.canId, [...(map.get(b.canId) ?? []), b]);
    return map;
  }, [barcodes.data]);

  const shown = useMemo(() => {
    const list = query.trim() ? searchCans(state.cans, state.categories, query) : state.cans;
    return filter === 'alle' ? list : list.filter((c) => c.categoryId === filter);
  }, [query, filter, state.cans, state.categories]);

  const countByCat = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of state.cans) map.set(c.categoryId, (map.get(c.categoryId) ?? 0) + 1);
    return map;
  }, [state.cans]);

  // A search shows one ranked list; browsing groups by category.
  const groups = query.trim()
    ? [{ id: 'results', name: `${shown.length} gevonden`, items: shown }]
    : state.categories
        .map((cat) => ({ id: cat.id, name: cat.name, items: shown.filter((c) => c.categoryId === cat.id) }))
        .filter((g) => g.items.length > 0);

  const open = openId === null ? null : (state.cans.find((c) => c.id === openId) ?? null);

  return (
    <main className="screen screen--padded">
      <div style={{ padding: '0 20px' }}>
        <button type="button" className="back-link" onClick={onBack}>
          <span aria-hidden="true">‹</span> Overig
        </button>
        <h1 className="title">
          DATABASE
        </h1>
        <div className="lede">
          {state.cans.length} soorten
          {barcodes.data ? ` · ${barcodes.data.length} barcodes` : ''}
        </div>
        <input
          className="input"
          style={{ marginTop: 16 }}
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          placeholder="Zoek op naam of reeks"
          aria-label="Zoek in de database"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="filter-row filter-row--db no-scrollbar" role="toolbar" aria-label="Filter op reeks">
        {[{ id: 'alle', name: 'Alle' }, ...state.categories.filter((c) => countByCat.has(c.id))].map((c) => (
          <button
            key={c.id}
            type="button"
            className="chip filter-chip"
            aria-pressed={filter === c.id}
            onClick={() => setFilter(c.id)}
          >
            {c.name}
            {c.id !== 'alle' && <small> {countByCat.get(c.id)}</small>}
          </button>
        ))}
      </div>

      {groups.map((g) => (
        <section key={g.id} className="group">
          <h2 className="label" style={{ margin: 0, paddingBottom: 8 }}>
            {g.name}
            {g.id !== 'results' && ` · ${g.items.length}`}
          </h2>
          <div className="rows">
            {g.items.map((c) => {
              const eans = eansByCan.get(c.id)?.length ?? 0;
              const status = [
                eans ? `${eans} ${eans === 1 ? 'barcode' : 'barcodes'}` : null,
                owned.has(c.id) ? '✓ in je collectie' : wanted.has(c.id) ? 'op je zoeklijst' : null,
              ].filter(Boolean);
              return (
                <button key={c.id} type="button" className="row row--button" onClick={() => setOpenId(c.id)}>
                  <span className="row__thumb">
                    <CanImage imageId={c.imageId} empty="blank" />
                  </span>
                  <span className="row__body">
                    <span className="row__name">{c.name}</span>
                    {status.length > 0 && <span className="row__sub">{status.join(' · ')}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {groups.length === 0 && (
        <div className="group">
          <div className="lede">Niets gevonden. Staat de smaak er echt niet tussen?</div>
        </div>
      )}

      <div className="add-flavor">
        <button type="button" className="btn btn--outline" style={{ width: '100%' }} onClick={onNewFlavor}>
          Smaak toevoegen
        </button>
        <p className="credit">
          Soorten en blikfoto's: <a href="https://beastvault.app" target="_blank" rel="noreferrer">BeastVault</a>.
          Barcodes: <a href="https://openfoodfacts.org" target="_blank" rel="noreferrer">Open Food Facts</a> (ODbL) en
          je eigen scans.
        </p>
      </div>

      {open && (
        <CanSheet
          key={open.id}
          state={state}
          can={open}
          eans={eansByCan.get(open.id) ?? []}
          owned={owned.has(open.id)}
          wanted={wanted.has(open.id)}
          onClose={() => setOpenId(null)}
          onAddToWall={() => onAddToWall(open.id)}
          onWanted={(w) => onWanted(open.id, w)}
          onError={onError}
        />
      )}
    </main>
  );
}

interface SheetProps {
  state: AppState;
  can: Can;
  eans: Barcode[];
  owned: boolean;
  wanted: boolean;
  onClose: () => void;
  onAddToWall: () => void;
  onWanted: (wanted: boolean) => void;
  onError: (message: string) => void;
}

function CanSheet({ state, can, eans, owned, wanted, onClose, onAddToWall, onWanted, onError }: SheetProps) {
  const category = state.categories.find((c) => c.id === can.categoryId);
  const upload = useUploadCanImage();
  const unlink = useDeleteBarcode();
  const fileInput = useRef<HTMLInputElement>(null);
  const [confirmEan, setConfirmEan] = useState<string | null>(null);
  const onWall = state.entries.filter((e) => e.canId === can.id).length;

  return (
    <Sheet label={can.name} onClose={onClose}>
      {(close) => (
        <>
          <div className="sheet__top">
            <button
              type="button"
              className="sheet__img"
              onClick={() => fileInput.current?.click()}
              aria-label={can.imageId ? 'Eigen foto kiezen' : 'Foto toevoegen'}
              disabled={upload.isPending}
            >
              <CanImage imageId={can.imageId} alt={can.name} />
              {(!can.imageId || upload.isPending) && (
                <span className="sheet__img-hint">{upload.isPending ? 'uploaden…' : '+ foto'}</span>
              )}
            </button>
            <div className="sheet__head">
              <div className="label label--green">{category?.name}</div>
              <h2 className="sheet__name">{can.name}</h2>
              <div className="sheet__meta">
                {owned ? `${onWall}× in je collectie` : wanted ? 'Op je zoeklijst' : 'Nog niet in je verzameling'}
              </div>
            </div>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate({ id: can.id, file }, { onError: (err) => onError(err.message) });
              e.target.value = '';
            }}
          />

          <div className="label section-label">Barcodes</div>
          {eans.length === 0 ? (
            <div className="note">
              Nog geen barcode bekend. Scan het blik, dan onthoudt de app hem.
            </div>
          ) : (
            <ul className="ean-list">
              {eans.map((b) => (
                <li key={b.ean} className="ean-row">
                  <div className="ean-row__body">
                    <div className="ean-row__code">{b.ean}</div>
                    <div className="ean-row__meta">
                      {[b.country ? countryCode(b.country) : null, b.sizeMl ? sizeLabel(b.sizeMl) : null, b.source === 'off' ? 'Open Food Facts' : 'eigen scan']
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {b.label && <div className="ean-row__label">{b.label}</div>}
                  </div>
                  {confirmEan === b.ean ? (
                    <button
                      type="button"
                      className="pill pill--danger"
                      disabled={unlink.isPending}
                      onClick={() =>
                        unlink.mutate(b.ean, {
                          onSuccess: () => setConfirmEan(null),
                          onError: (err) => onError(err.message),
                        })
                      }
                    >
                      Zeker?
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="pill"
                      aria-label={`Barcode ${b.ean} ontkoppelen`}
                      onClick={() => setConfirmEan(b.ean)}
                    >
                      Ontkoppel
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="sheet-actions">
            <button type="button" className="btn btn--outline" onClick={() => onWanted(!wanted)} disabled={owned && !wanted}>
              {wanted ? 'Van zoeklijst' : 'Op zoeklijst'}
            </button>
            <button
              type="button"
              className="btn-done"
              onClick={() => {
                close();
                onAddToWall();
              }}
            >
              In collectie
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}
